import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import { PersonaHtmlContent } from '@/components/shared/PersonaHtmlContent';
import {
  marketAnalysesApi,
  personasApi,
  positioningsApi,
  businessModelsApi,
  mvpPlansApi,
  marketingStrategiesApi,
  financialModelsApi,
  methodologiesApi,
} from '@/utils/api-pb';
import { extractPersonaName } from '@/utils/markdown';
import { ARTIFACT_CONFIG, methodologyPagePath, type ArtifactReference } from './artifact-config';

interface ArtifactPanelProps {
  artifact: ArtifactReference;
  onClose: () => void;
}

// Загружает текст артефакта по типу и id записи
async function loadArtifactContent(
  artifact: ArtifactReference
): Promise<{ content: string; subtitle?: string; pagePath?: string }> {
  const { type, recordId } = artifact;

  switch (type) {
    case 'market_analysis': {
      const record = await marketAnalysesApi.getOne(recordId);
      return { content: record.generated_text || '' };
    }
    case 'persona': {
      const record = await personasApi.getOne(recordId);
      return { content: record.generated_text || '', subtitle: extractPersonaName(record) };
    }
    case 'positioning': {
      const record = await positioningsApi.getOne(recordId);
      return { content: record.generated_text || '' };
    }
    case 'business_model': {
      const record = await businessModelsApi.getOne(recordId);
      return { content: record.generated_text || '' };
    }
    case 'mvp_plan': {
      const record = await mvpPlansApi.getOne(recordId);
      return { content: record.generated_text || '' };
    }
    case 'marketing_strategy': {
      const record = await marketingStrategiesApi.getOne(recordId);
      return { content: record.generated_text || '' };
    }
    case 'financial_model': {
      const record = await financialModelsApi.getOne(recordId);
      return { content: record.ai_recommendations || '', subtitle: record.model_name };
    }
    case 'methodology': {
      // One artifact type covers every methodology; the page to open depends on which one.
      const record = await methodologiesApi.getOne(recordId);
      return {
        content: record.generated_text || '',
        subtitle: record.title,
        pagePath: methodologyPagePath(record.method, record.id),
      };
    }
    default:
      return { content: '' };
  }
}

export default function ArtifactPanel({ artifact, onClose }: ArtifactPanelProps) {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const { localizedPath } = useLanguage();

  const [content, setContent] = useState<string>('');
  const [subtitle, setSubtitle] = useState<string | undefined>();
  const [pagePath, setPagePath] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const config = ARTIFACT_CONFIG[artifact.type];

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setHasError(false);
    setContent('');
    setSubtitle(undefined);
    setPagePath(undefined);

    loadArtifactContent(artifact)
      .then((result) => {
        if (cancelled) return;
        setContent(result.content);
        setSubtitle(result.subtitle);
        setPagePath(result.pagePath);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [artifact.type, artifact.recordId]);

  // Закрытие по Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!config) return null;

  const Icon = config.icon;
  const fullPagePath = pagePath ?? (artifact.recordId
    ? `${config.path}?recordId=${artifact.recordId}`
    : config.path);

  return (
    <aside
      className="fixed inset-0 z-50 flex flex-col bg-white md:static md:z-auto md:flex-1 md:basis-0 md:min-w-0 md:border-l md:border-[var(--grey-accent,#eeeeee)]"
      role="complementary"
      aria-label={t(config.labelKey)}
    >
      {/* Шапка */}
      <div className="flex items-start gap-[12px] px-[20px] py-[16px] border-b border-[var(--grey-accent,#eeeeee)]">
        <div className={`flex-shrink-0 mt-[2px] ${config.color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-manrope font-semibold text-[16px] leading-[22px] text-[color:var(--black-text,#0d0d0d)] truncate">
            {t(config.labelKey)}
          </h2>
          {subtitle && (
            <p className="font-segoe text-[13px] leading-[18px] text-[color:var(--grey-text,#565858)] truncate">
              {subtitle}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          aria-label={t('artifactPanel.close')}
          className="flex-shrink-0 p-[4px] rounded-[8px] text-[var(--grey-text,#565858)] hover:bg-[var(--grey-accent,#eeeeee)] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Содержимое */}
      <div className="flex-1 overflow-y-auto px-[24px] py-[20px] md:px-[32px]">
        {isLoading ? (
          <div className="flex items-center gap-2 justify-center py-8 text-[color:var(--grey-text,#565858)]">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="font-segoe text-[14px]">{t('artifactPanel.loading')}</span>
          </div>
        ) : hasError ? (
          <p className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)] py-4 text-center">
            {t('artifactPanel.error')}
          </p>
        ) : content.trim() ? (
          artifact.type === 'persona' ? (
            <PersonaHtmlContent html={content} />
          ) : (
            <MarkdownRenderer content={content} />
          )
        ) : (
          <p className="font-segoe text-[14px] leading-[20px] text-[color:var(--grey-text,#565858)] py-4 text-center">
            {t('artifactPanel.empty')}
          </p>
        )}
      </div>

      {/* Футер */}
      <div className="px-[20px] py-[16px] border-t border-[var(--grey-accent,#eeeeee)]">
        <button
          onClick={() => navigate(localizedPath(fullPagePath))}
          className="w-full flex items-center justify-center gap-[8px] px-[16px] py-[10px] rounded-[12px] bg-[rgba(33,151,241,0.15)] text-[#2197f1] hover:bg-[#2197f1] hover:text-white transition-colors"
        >
          <ExternalLink className="h-4 w-4" />
          <span className="font-segoe font-semibold text-[14px] leading-[20px] text-current">
            {t('artifactPanel.openFull')}
          </span>
        </button>
      </div>
    </aside>
  );
}
